import React, { useState, useEffect } from 'react';
import { Pizza, Pizzeria, MapCenter, Location, MapBounds } from '../types';
import { useFirestore } from '../hooks/useFirestore';
import SearchBar from '../components/SearchBar';
import Map from '../components/Map';
import PizzaCard from '../components/PizzaCard';
import PizzeriaCard from '../components/PizzeriaCard';
import OfflineIndicator from '../components/OfflineIndicator';
import NoResults from '../components/NoResults';
import { useNavigate } from 'react-router-dom';
import { WelcomeModal } from '../components/WelcomeModal';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';

interface PizzeriaWithCoords extends Omit<Pizzeria, 'lat' | 'lng'> {
  lat: number;
  lng: number;
}

export default function Home() {
  const [showPizzerie, setShowPizzerie] = useState(true);
  const [pizzerias, setPizzerias] = useState<Pizzeria[]>([]);
  const [filteredPizzerias, setFilteredPizzerias] = useState<Pizzeria[]>([]);
  const [pizzas, setPizzas] = useState<Pizza[]>([]);
  const [filteredPizzas, setFilteredPizzas] = useState<Pizza[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { searchPizzerias, searchPizzas, isOffline, mapCenter } = useFirestore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [bounds, setBounds] = useState<MapBounds>({
    south: 35,
    north: 47,
    east: 19,
    west: 6
  });
  const [isWelcomeModalVisible, setWelcomeModalVisible] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchInitialData = async () => {
      const initialPizzerias = await searchPizzerias({ type: 'pizzeria', query: '' });
      const initialPizzas = await searchPizzas({ type: 'pizza', query: '' });
      setPizzerias(initialPizzerias);
      setFilteredPizzerias(initialPizzerias);
      setPizzas(initialPizzas);
      setFilteredPizzas(initialPizzas);
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const checkFirstVisit = async () => {
      console.log('Checking first visit...', { user });
      
      // Reset dello stato
      setWelcomeModalVisible(false);

      // Se non c'è utente o non è una pizzeria, esci subito
      if (!user || user.role !== 'pizzeria') {
        console.log('User check failed:', { user });
        return;
      }

      try {
        console.log('Checking pizzerie for user:', user.uid);
        
        // Controlla se l'utente ha già delle pizzerie
        const pizzerieRef = collection(db, 'pizzerie');
        const q = query(pizzerieRef, where('ownerId', '==', user.uid));
        const snapshot = await getDocs(q);
        
        console.log('Pizzerie found:', snapshot.size);

        // Se l'utente ha già delle pizzerie, esci
        if (snapshot.size > 0) {
          console.log('User has pizzerie, not showing modal');
          localStorage.setItem(`hasVisitedPizzerie_${user.uid}`, 'true');
          return;
        }

        // Controlla il localStorage
        const hasVisited = localStorage.getItem(`hasVisitedPizzerie_${user.uid}`);
        console.log('Has visited check:', { hasVisited });

        if (!hasVisited) {
          console.log('Showing welcome modal');
          setWelcomeModalVisible(true);
          localStorage.setItem(`hasVisitedPizzerie_${user.uid}`, 'true');
        } else {
          console.log('User has already visited, not showing modal');
        }
      } catch (error) {
        console.error('Error checking pizzerie:', error);
      }
    };

    checkFirstVisit();
  }, [user]);

  const handleSearch = async (query: string, type: 'pizzeria' | 'pizza' | 'location') => {
    setSearchQuery(query);
    setLoading(true);
    try {
      if (type === 'pizza') {
        const results = await searchPizzas({ type, query });
        setPizzas(results);
        setFilteredPizzas(results);
        setShowPizzerie(false);
      } else {
        const results = await searchPizzerias({ type, query });
        setPizzerias(results);
        setFilteredPizzerias(results);
        setShowPizzerie(true);
      }
    } catch (error) {
      console.error('Errore nella ricerca:', error);
      setError('Si è verificato un errore durante la ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleBoundsChange = (bounds: [[number, number], [number, number]]) => {
    const [[south, west], [north, east]] = bounds;

    if (showPizzerie) {
      const visiblePizzerias = pizzerias.filter(pizzeria => {
        if (typeof pizzeria.lat !== 'number' || typeof pizzeria.lng !== 'number') {
          return false;
        }
        return (
          pizzeria.lat >= south && 
          pizzeria.lat <= north &&
          pizzeria.lng >= west && 
          pizzeria.lng <= east
        );
      });
      setFilteredPizzerias(visiblePizzerias);
    } else {
      const visiblePizzas = pizzas.filter(pizza => {
        const pizzeriasWithPizza = pizzerias.filter(pizzeria => {
          if (typeof pizzeria.lat !== 'number' || typeof pizzeria.lng !== 'number') {
            return false;
          }
          return (
            pizzeria.pizzas.includes(pizza.id) &&
            pizzeria.lat >= south && 
            pizzeria.lat <= north &&
            pizzeria.lng >= west && 
            pizzeria.lng <= east
          );
        });
        return pizzeriasWithPizza.length > 0;
      });
      setFilteredPizzas(visiblePizzas);
    }
  };

  const getResultsCount = () => {
    return showPizzerie ? filteredPizzerias.length : filteredPizzas.length;
  };

  const isPizzeriaWithCoords = (pizzeria: Pizzeria): pizzeria is PizzeriaWithCoords => {
    return (
      pizzeria !== null &&
      typeof pizzeria === 'object' &&
      typeof (pizzeria as any).lat === 'number' &&
      typeof (pizzeria as any).lng === 'number' &&
      !isNaN((pizzeria as any).lat) &&
      !isNaN((pizzeria as any).lng)
    );
  };

  const isWithinBounds = (pizzeria: PizzeriaWithCoords) => {
    const { south, north, west, east } = bounds;
    return (
      pizzeria.lat >= south &&
      pizzeria.lat <= north &&
      pizzeria.lng >= west &&
      pizzeria.lng <= east
    );
  };

  const mapLocations: Location[] = pizzerias
    .filter(isPizzeriaWithCoords)
    .filter(isWithinBounds)
    .map(({ id, lat, lng, name }) => ({
      id,
      lat,
      lng,
      name
    }));

  const defaultCenter: MapCenter = {
    lat: 41.9028,
    lng: 12.4964,
    zoom: 6
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section con max-width */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Trova la tua pizza vegana preferita
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Scopri le migliori pizzerie con opzioni vegane nella tua zona
            </p>
            <div className="max-w-3xl mx-auto">
              <SearchBar onSearch={handleSearch} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="w-full px-4 py-8">
        {/* Results count */}
        <div className="max-w-7xl mx-auto mb-4">
          <div className="text-sm text-gray-500">
            {searchQuery && (
              <span>Ricerca: <span className="font-medium text-gray-700">{searchQuery}</span> • </span>
            )}
            <span>{getResultsCount()} risultat{getResultsCount() === 1 ? 'o' : 'i'}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Results Panel */}
          <div className="bg-white shadow-lg overflow-hidden">
            {error && (
              <div className="p-4 bg-red-50 text-red-600">{error}</div>
            )}
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pizzaveg mx-auto"></div>
                <p className="mt-4 text-gray-600">Caricamento...</p>
              </div>
            ) : (
              <div className="p-6">
                {getResultsCount() === 0 ? (
                  <NoResults query={searchQuery} type={showPizzerie ? 'pizzerie' : 'pizze'} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {showPizzerie ? (
                      filteredPizzerias.map(pizzeria => (
                        <PizzeriaCard key={pizzeria.id} pizzeria={pizzeria} />
                      ))
                    ) : (
                      filteredPizzas.map(pizza => (
                        <PizzaCard key={pizza.id} pizza={pizza} />
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Map Panel */}
          <div className="bg-white shadow-lg overflow-hidden">
            <div className="h-[600px]">
              <Map 
                locations={mapLocations}
                center={mapCenter || defaultCenter}
                onMarkerClick={(id) => navigate(`/pizzerie/${id}`)}
                onBoundsChange={handleBoundsChange}
              />
            </div>
          </div>
        </div>
      </div>

      <OfflineIndicator show={isOffline} />

      <WelcomeModal 
        isVisible={isWelcomeModalVisible}
        onClose={() => setWelcomeModalVisible(false)}
      />
    </div>
  );
}